import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useApi from '../hooks/useApi';
import { useTenant } from './TenantContext';
import { useOktaAuth } from '@okta/okta-react';
import { useCustomAlert, useErrorToast } from '../components/common';
import { getApiErrorMessage } from '../utils/errorMessages';
 
const ProjectProgressContext = createContext(null);
 
export const useProjectProgress = () => {
  const context = useContext(ProjectProgressContext);
  if (!context) {
    throw new Error('useProjectProgress must be used within ProjectProgressProvider');
  }
  return context;
};
 
// Change types for the tracker (used for display and to drive save UI visibility)
export const CHANGE_TYPES = {
  ADD_FILTER: 'add_filter',
  DELETE_FILTER: 'delete_filter',
  ADD_CARD: 'add_card',
  EDIT_CARD: 'edit_card',
  DELETE_CARD: 'delete_card',
  EDIT_DASHBOARD_NAME: 'edit_dashboard_name',
  UNSAVED: 'unsaved',
};
 
export const ProjectProgressProvider = ({ children }) => {
  const [currentProject, setCurrentProject] = useState(null);
  const [changesTracker, setChangesTracker] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [isEditingDashboard, setIsEditingDashboard] = useState(false);
  const api = useApi();
  const { currentTenant } = useTenant();
  const { authState } = useOktaAuth();
  const { showAlert, showConfirm } = useCustomAlert();
  const { showErrorToast } = useErrorToast();
  const saveInProgressRef = useRef(false);
  const saveBeforeLeaveRef = useRef(null);
  const hasBeenSavedOnceRef = useRef(false);
  const changeIdRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
 
  // Save progress UI (dialog, beforeunload, navigation guard) only when there are tracked changes
  const hasUnsavedChanges = changesTracker.length > 0;
 
  const registerSaveBeforeLeave = useCallback((fn) => {
    saveBeforeLeaveRef.current = fn;
  }, []);
  const unregisterSaveBeforeLeave = useCallback(() => {
    saveBeforeLeaveRef.current = null;
  }, []);
 
  // Initialize project from file upload metadata
  // Accepts file metadata from upload response (projectId, fileName, displayName, etc.)
  // options.markUnsaved: if false, do not add a change (e.g. when rehydrating from URL)
  // options.markAsSaved: if true, treat as already saved (e.g. opened from list) so "Don't Save" won't soft-delete
  const initializeProject = useCallback(async (fileMetadata, options = {}) => {
    if (fileMetadata) {
      setIsEditingDashboard(false);
      const projectName = fileMetadata.projectName || fileMetadata.displayName || 'Untitled Project';
      const fileName = fileMetadata.fileName;
      const displayName = fileMetadata.displayName || projectName;
      const projectId = fileMetadata.projectId ?? fileMetadata.sessionId;
      const currentStep = fileMetadata.currentStep || 'preview';
      hasBeenSavedOnceRef.current = options.markAsSaved === true;
 
      setCurrentProject({
        projectId,
        projectName: (projectName || '').trim() || 'Project',
        displayName: (displayName || '').trim() || 'Project',
        fileName: fileName || '',
        currentStep: currentStep,
        fileMetadata: fileMetadata,
        SourceSurveyId: fileMetadata.SourceSurveyId || fileMetadata.sourceSurveyId,
        isSavedProject: !!(projectId && fileMetadata.currentStep),
      });
      // Don't add to changesTracker on init; save UI shows only when user performs an action (add filter, card, etc.)
    }
  }, []);
 
  // Update current step
  const updateCurrentStep = useCallback((step) => {
    if (currentProject && step) {
      const normalizedStep = step.toLowerCase().trim();
      const validSteps = ['preview', 'filter', 'cards', 'dashboard'];
      if (validSteps.includes(normalizedStep)) {
        setCurrentProject(prev => ({
          ...prev,
          currentStep: normalizedStep,
        }));
      } else {
        console.warn('Invalid step provided to updateCurrentStep:', step, 'Valid steps:', validSteps);
      }
    }
  }, [currentProject]);
 
  // Track a user action so save progress UI is shown only when there are changes
  const trackChange = useCallback((type, label) => {
    if (!currentProject) return;
    const id = ++changeIdRef.current;
    setChangesTracker(prev => [...prev, { id, type, label: label || type, timestamp: Date.now() }]);
  }, [currentProject]);
 
  const clearChangesTracker = useCallback(() => {
    setChangesTracker([]);
  }, []);
 
  const markProjectAsSaved = useCallback(() => {
    hasBeenSavedOnceRef.current = true;
  }, []);
 
  // Clear only changes of a given type (e.g. so Filters can clear ADD_FILTER when user unchecks all)
  const clearChangesByType = useCallback((type) => {
    setChangesTracker(prev => prev.filter(c => c.type !== type));
  }, []);
 
  // Mark that changes have been made (adds a generic entry; prefer trackChange(type, label) for specific actions)
  const markUnsavedChanges = useCallback(() => {
    if (currentProject) {
      trackChange(CHANGE_TYPES.UNSAVED, 'Change');
    }
  }, [currentProject, trackChange]);
 
  // Save project progress to database
  const saveProjectProgress = useCallback(async (step = null) => {
    if (!currentProject || !currentTenant || saveInProgressRef.current) {
      return { success: false };
    }
 
    try {
      saveInProgressRef.current = true;
      const userEmail = authState?.idToken?.claims?.email || authState?.idToken?.claims?.sub;
 
      // Get UserId from email (required for UserProjectMapping)
      // Use search parameter to find user by email
      const userResponse = await api.get(`/users?search=${encodeURIComponent(userEmail)}`);
 
      if (!userResponse.data?.success || !userResponse.data.data?.users || userResponse.data.data.users.length === 0) {
        console.error('User not found for email:', userEmail);
        return { success: false, error: 'User not found' };
      }
 
      // Find the user matching the email (case-insensitive)
      const user = userResponse.data.data.users.find(
        u => u.email && u.email.toLowerCase() === userEmail.toLowerCase()
      );
 
      if (!user || !user.userId) {
        console.error('User not found for email:', userEmail);
        return { success: false, error: 'User not found' };
      }
     
      const userId = user.userId;
 
      // Project already exists (created during upload) - just update ProjectStep
      if (!currentProject.projectId) {
        return { success: false, error: 'Project ID not found' };
      }
 
      const projectId = currentProject.projectId;
     
      // Determine the step to save - validate it's a valid step
      const stepToSave = (step || currentProject.currentStep || 'preview').toLowerCase();
      const validSteps = ['preview', 'filter', 'cards', 'dashboard'];
     
      if (!validSteps.includes(stepToSave)) {
        console.error('Invalid ProjectStep:', stepToSave, 'Valid steps:', validSteps);
        return { success: false, error: `Invalid step: ${stepToSave}. Must be one of: ${validSteps.join(', ')}` };
      }
     
      // Only update ProjectStep (project was already created during upload)
      console.log('Saving project progress:', { projectId, stepToSave });
      const updateResponse = await api.put(`/projects/${projectId}`, {
        ProjectStep: stepToSave,
      });
 
      console.log('Update response:', updateResponse.data);
 
      if (updateResponse.data?.success) {
        const updatedProject = updateResponse.data.data;
        console.log('Project updated successfully:', updatedProject);
        console.log('Updated ProjectStep:', updatedProject?.ProjectStep || updatedProject?.projectStep);
       
        hasBeenSavedOnceRef.current = true;
        setChangesTracker([]);
        // Update currentProject with the saved step
        setCurrentProject(prev => ({ ...prev, currentStep: stepToSave }));
        return { success: true, projectId };
      }
 
      const errorMessage = getApiErrorMessage(null, 'Failed to save project');
      console.error('Failed to save project progress:', errorMessage, updateResponse.data);
      return { success: false, error: errorMessage };
    } catch (error) {
      console.error('Error saving project progress:', error);
      return { success: false, error: getApiErrorMessage(error, 'Failed to save project') };
    } finally {
      saveInProgressRef.current = false;
    }
  }, [currentProject, currentTenant, authState, api]);
 
  // Handle save dialog confirmation: run page save (filters/widgets/dashboard name), then project step, then navigate
  const handleSaveConfirm = useCallback(async () => {
    const targetPath = pendingNavigation;
    setSaving(true);
 
    try {
      if (saveBeforeLeaveRef.current) {
        const pageResult = await saveBeforeLeaveRef.current();
        if (pageResult && pageResult.success === false) {
          const errMsg = pageResult.error || 'Failed to save changes.';
          showErrorToast(errMsg);
          setSaving(false);
          return;
        }
      }
      const result = await saveProjectProgress();
      setShowSaveDialog(false);
      setPendingNavigation(null);
      setSaving(false);
      if (result.success) {
        if (targetPath) navigate(targetPath);
      } else {
        const errorMsg = result.error || 'Failed to save progress. You may lose unsaved changes.';
        showErrorToast(`Failed to save progress: ${errorMsg}`);
        if (targetPath) navigate(targetPath);
      }
    } catch (err) {
      console.error('Save before leave failed:', err);
      showErrorToast(getApiErrorMessage(err, 'Failed to save changes.'));
      setSaving(false);
      setPendingNavigation(targetPath);
      setShowSaveDialog(true);
    }
  }, [saveProjectProgress, pendingNavigation, navigate]);
 
  // Handle save dialog cancellation (user chose "Don't Save")
  // If the project was never saved in this session, soft-delete it so it doesn't appear in the projects list.
  // When we discard an unsaved project, navigate to landing instead of the target step so the user doesn't
  // land on a page (e.g. Filters) that would try to load the now-deleted project and show "Table not found".
  const handleSaveCancel = useCallback(async () => {
    const projectId = currentProject?.projectId;
    const targetPath = pendingNavigation;
    const shouldSoftDelete = projectId && !hasBeenSavedOnceRef.current;
 
    if (shouldSoftDelete) {
      const confirmed = await showConfirm(
        'This will delete the unsaved project and any changes made in this session. Continue?',
        { title: 'Delete Unsaved Project', confirmText: 'Delete' }
      );
      if (!confirmed) {
        return;
      }
      try {
        await api.delete(`/projects/${projectId}`);
      } catch (err) {
        console.error('Failed to soft-delete unsaved project:', err);
      }
      setShowSaveDialog(false);
      setChangesTracker([]);
      setCurrentProject(null);
      setPendingNavigation(null);
      // Discarded project no longer exists; go to upload so user doesn't hit "Table not found" on Filters/Cards/etc.
      navigate('/landing');
      return;
    }
 
    setShowSaveDialog(false);
    setChangesTracker([]);
    setCurrentProject(null);
    setPendingNavigation(null);
    if (targetPath) {
      navigate(targetPath);
    }
  }, [currentProject?.projectId, pendingNavigation, navigate, api, showConfirm]);
 
  // Handle save dialog dismissal (user clicked the X / wants to go back to their work).
  // Just close the dialog and cancel the pending navigation: keep unsaved changes intact
  // and stay on the current page.
  const handleSaveDismiss = useCallback(() => {
    if (saving) return;
    setShowSaveDialog(false);
    setPendingNavigation(null);
  }, [saving]);
 
  const PROJECT_RELATED_PAGES = [
    '/landing', '/preview', '/filters', '/cards', '/dashboard',
  ];
  const isProjectRelatedPage = useCallback((path) => {
    if (!path) return false;
    if (path === '/landing') return true;
    return PROJECT_RELATED_PAGES.some(projectPage =>
      path.startsWith(projectPage + '/') || path === projectPage
    );
  }, []);
 
  // Navigation guard: block when there are unsaved changes and user is navigating away (including to another step)
  const checkNavigation = useCallback((targetPath) => {
    if (!hasUnsavedChanges || !currentProject) return true;
    if (!isProjectRelatedPage(location.pathname)) return true;
    if (targetPath === location.pathname) return true;
    setPendingNavigation(targetPath);
    setShowSaveDialog(true);
    return false;
  }, [hasUnsavedChanges, currentProject, location.pathname, isProjectRelatedPage]);
 
  // Browser beforeunload handler
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && currentProject) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
 
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, currentProject]);
 
  // Intercept navigation attempts - both link clicks and programmatic navigation
  useEffect(() => {
    if (!hasUnsavedChanges || !currentProject) {
      return;
    }
 
    // Only show save dialog if we're currently on a project-related page
    const isCurrentlyOnProjectPage = isProjectRelatedPage(location.pathname);
    if (!isCurrentlyOnProjectPage) {
      return; // Don't block navigation if we're not on a project page
    }
 
    // Intercept link clicks: block when navigating to a different path (including another step) if unsaved
    const handleClick = (e) => {
      const link = e.target.closest('a[href]');
      if (link && link.href) {
        const url = new URL(link.href);
        const targetPath = url.pathname;
        if (targetPath.startsWith('/') && !targetPath.startsWith('//') && targetPath !== location.pathname) {
          if (checkNavigation(targetPath) === false) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }
    };
 
    window.__checkNavigation = checkNavigation;
 
    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      if (window.__checkNavigation) delete window.__checkNavigation;
    };
  }, [hasUnsavedChanges, currentProject, location.pathname, checkNavigation]);
 
  // Clear project when navigating away from project pages
  const clearProject = useCallback(() => {
    setCurrentProject(null);
    setChangesTracker([]);
    setIsEditingDashboard(false);
  }, []);
 
  const value = {
    currentProject,
    hasUnsavedChanges,
    changesTracker,
    trackChange,
    clearChangesTracker,
    clearChangesByType,
    showSaveDialog,
    setShowSaveDialog,
    setPendingNavigation,
    saving,
    isEditingDashboard,
    setIsEditingDashboard,
    initializeProject,
    updateCurrentStep,
    saveProjectProgress,
    clearProject,
    handleSaveConfirm,
    handleSaveCancel,
    handleSaveDismiss,
    checkNavigation,
    markUnsavedChanges,
    registerSaveBeforeLeave,
    unregisterSaveBeforeLeave,
    markProjectAsSaved,
  };
 
  return (
    <ProjectProgressContext.Provider value={value}>
      {children}
    </ProjectProgressContext.Provider>
  );
};
 